import React, { useState, useEffect, useRef } from 'react';
import { Plus } from '@phosphor-icons/react';
import { createId } from '@paralleldrive/cuid2';

import { Filter, FilterOption, FilterRules, FilterValue, Operator } from './index';
import { Button } from '../ui/button';

import { cn } from '@/lib/utils';

interface FiltersProps extends React.HTMLAttributes<HTMLDivElement> {
  filters: FilterValue[];
  onFilterChange: (filter: FilterValue) => void;
  onFilterRemove: (filterId: string | number) => void;
  onFilterAdd?: (filter: FilterValue) => void;
  sourceOptions: FilterOption[];
  rules: FilterRules;
}

const Filters = ({
  filters,
  onFilterChange,
  onFilterRemove,
  onFilterAdd,
  sourceOptions,
  rules,
  className,
}: FiltersProps) => {
  const [internalFilters, setInternalFilters] = useState<FilterValue[]>(filters || []);
  const filterRefs = useRef<{ [key: string]: { focus: () => void } | null }>({});

  // Sync internal state with props
  useEffect(() => {
    setInternalFilters(filters || []);
  }, [filters]);

  // Get sources that are already in use
  const usedSources = internalFilters.map((filter) => filter.source).filter(Boolean);

  // Get available sources that haven't been used yet
  const availableSources = sourceOptions.filter((option) => {
    // If the source has multiSelect enabled, filter out used sources
    if (rules[option.id]?.multiSelect) {
      return !usedSources.includes(option.id);
    }

    // If multiSelect is false or undefined, allow the source to be used multiple times
    return sourceOptions;
  });

  // Check if we can add more filters
  const canAddFilter = availableSources.length > 0;

  const handleAddFilter = () => {
    if (!canAddFilter) return;

    const newFilter = {
      id: createId(),
      source: availableSources[0].id,
      operator: Operator.IS,
      value: rules[availableSources[0].id]?.multiSelect ? [] : '',
    };

    const newFilters = [...internalFilters, newFilter];
    setInternalFilters(newFilters);

    // Focus the new filter after it's rendered
    requestAnimationFrame(() => {
      filterRefs.current[newFilter.id]?.focus();
    });

    // Notify parent if callback provided
    onFilterAdd?.(newFilter);
  };

  const handleRemoveFilter = (filter: FilterValue) => {
    const newFilters = internalFilters.filter((f) => f.id !== filter.id);
    setInternalFilters(newFilters);
    onFilterRemove(filter.id);
  };

  return (
    <fieldset className={cn('flex flex-wrap gap-2', className)}>
      <legend className="sr-only">Filters</legend>
      {internalFilters?.map((filter) => {
        // For each filter, include available sources plus its own current source
        const filterSourceOptions = sourceOptions.filter(
          (option) =>
            !usedSources.includes(option.id) || // Available sources
            option.id === filter.source, // Or its own current source
        );

        return (
          <Filter
            key={filter.id}
            ref={(ref) => {
              filterRefs.current[filter.id] = ref;
            }}
            filter={filter}
            sourceOptions={filterSourceOptions}
            rules={rules}
            onRemoveFilter={() => handleRemoveFilter(filter)}
            onFilterChange={onFilterChange}
          />
        );
      })}
      {canAddFilter && (
        <Button
          onClick={handleAddFilter}
          variant="ghost"
          className="border border-foreground rounded-full gap-1"
          size="sm"
          title="Add filter"
        >
          <Plus size={16} />
          Add filter
        </Button>
      )}
    </fieldset>
  );
};

export { Filters };
